'use client';

import { announce } from '@react-aria/live-announcer';
import { useUpdateEffect } from '@react-aria/utils';
import { useControlledState } from '@react-stately/utils';
import type { ForwardedRef } from 'react';
import { forwardRef } from '../../forward-ref.js';
import { useLocalizedMessages } from '../../i18n/index.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import { withStyleProps } from '../../with-style-props.js';
import type { NavigationProps } from '../types.js';
import { ActionPagination } from './action-pagination.js';
import type { BasePaginationProps } from './base-pagination.js';
import { LinkPagination } from './link-pagination.js';

export interface PaginationProps extends Omit<BasePaginationProps, 'currentPage'>, Omit<NavigationProps, 'href'> {
  /** The current page number (controlled). Should be between `1` and `pageCount`. */
  currentPage?: number;
  /** The default page number (uncontrolled). Should be between `1` and `pageCount`. */
  defaultPage?: number;
  /** A callback function that is called when the page number changes. */
  onPageChange?: (page: number) => void;
  /** A function that returns a URL for a given page number. */
  href?: (page: number) => string;
}

function Pagination(
  {
    currentPage,
    defaultPage,
    href,
    onPageChange,

    // Navigation props
    hrefLang,
    target,
    download,
    ping,
    referrerPolicy,
    rel,
    routerOptions,

    ...props
  }: PaginationProps,
  ref: ForwardedRef<HTMLElement>,
) {
  const [page, setPage] = useControlledState(currentPage, defaultPage ?? 1, onPageChange);

  const { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy } = props;

  useProductionWarning(() => {
    if (!ariaLabel && !ariaLabelledBy) {
      console.warn('Pagination requires one of aria-label / aria-labelledby for accessibility');
    }
  }, [ariaLabel, ariaLabelledBy]);

  const messages = useLocalizedMessages('pagination');
  const selectedPageAnnouncement = messages.format('selectedPageAnnouncement', { pageNumber: page });

  // Announce when the selected page changes
  useUpdateEffect(() => {
    announce(selectedPageAnnouncement, 'polite', 4000);
  }, [selectedPageAnnouncement]);

  if (href) {
    return (
      <LinkPagination
        {...props}
        currentPage={page}
        onPageChange={setPage}
        navRef={ref}
        href={href}
        hrefLang={hrefLang}
        target={target}
        download={download}
        ping={ping}
        referrerPolicy={referrerPolicy}
        rel={rel}
        routerOptions={routerOptions}
      />
    );
  } else {
    return <ActionPagination {...props} navRef={ref} currentPage={page} onPageChange={setPage} />;
  }
}

/**
 * Allows navigating across multiple pages of related content.
 *
 * See [pagination usage guidelines](https://ui.cimpress.io/components/pagination/).
 */
const _Pagination = withStyleProps(forwardRef(Pagination), 'Pagination');

export { _Pagination as Pagination };
